export const mockComplianceCalendarResponse = {
  'current_regulations': [
    {
      'deadlines': 'Ongoing compliance; annual review of processing activities recommended',
      'department': 'Legal/IT/Data Protection Officer',
      'description': 'Comprehensive data protection framework governing the processing of personal data of EU citizens.',
      'key_requirements': [
        'Maintain records of processing activities',
        'Implement appropriate technical and organizational measures',
        'Conduct Data Protection Impact Assessments for high-risk processing',
        'Report data breaches within 72 hours',
        'Ensure lawful basis for processing personal data',
        'Honor data subject rights (access, erasure, portability, etc.)'
      ],
      'name': 'General Data Protection Regulation (GDPR)'
    },
    {
      'deadlines': 'Annual emissions report by March 31; surrender of allowances by April 30',
      'department': 'Environmental Health & Safety/Operations',
      'description': 'Cap-and-trade system for greenhouse gas emissions from energy-intensive industries.',
      'key_requirements': [
        'Monitor and report annual CO2 emissions',
        'Surrender sufficient allowances to cover emissions',
        'Maintain monitoring plan and ensure verification of emissions reports'
      ],
      'name': 'EU Emissions Trading System (EU ETS)'
    },
    {
      'deadlines': 'Permit renewal typically every 5-10 years; regular emissions monitoring and reporting',
      'department': 'Environmental Health & Safety/Operations',
      'description': 'Framework for permitting and controlling emissions from industrial installations.',
      'key_requirements': [
        'Obtain and comply with integrated environmental permit',
        'Implement Best Available Techniques (BAT)',
        'Monitor and report emissions to air, water, and soil',
        'Prevent and manage waste according to waste hierarchy'
      ],
      'name': 'Industrial Emissions Directive (IED)'
    },
    {
      'deadlines': 'Ongoing compliance; updates to Safety Data Sheets as needed',
      'department': 'Product Safety/R&D/Operations',
      'description': 'Framework for chemical substances management and safe use.',
      'key_requirements': [
        'Register manufactured/imported substances >1 tonne/year',
        'Communicate information on hazardous substances through supply chain',
        'Apply for authorization for substances of very high concern',
        'Comply with restrictions on certain chemical substances'
      ],
      'name': 'REACH Regulation (Registration, Evaluation, Authorization and Restriction of Chemicals)'
    },
    {
      'deadlines': 'Before placing products on the market; documentation must be maintained for 10 years',
      'department': 'Product Safety/R&D/Quality',
      'description': 'Framework ensuring health and safety requirements for machinery design and construction.',
      'key_requirements': [
        'Conduct risk assessment for manufactured machinery',
        'Prepare technical documentation',
        'Issue Declaration of Conformity',
        'Apply CE marking'
      ],
      'name': 'Machinery Directive'
    },
    {
      'deadlines': 'Ongoing compliance; annual waste reporting typically required',
      'department': 'Environmental Health & Safety/Operations',
      'description': 'Framework for waste management emphasizing waste prevention, reuse, and recycling.',
      'key_requirements': [
        'Apply waste hierarchy in waste management',
        'Ensure proper waste classification and handling',
        'Use authorized waste collectors and treatment facilities',
        'Maintain waste transfer documentation'
      ],
      'name': 'Waste Framework Directive'
    },
    {
      'deadlines': 'Ongoing compliance; regular review of risk assessments',
      'department': 'Human Resources/Health & Safety',
      'description': 'Minimum health and safety requirements for the workplace.',
      'key_requirements': [
        'Conduct workplace risk assessments',
        'Implement preventive measures',
        'Provide information and training to workers',
        'Ensure worker consultation on health and safety matters',
        'Maintain accident records'
      ],
      'name': 'Occupational Safety and Health Framework Directive'
    },
    {
      'deadlines': 'Energy audits every 4 years; annual energy consumption reporting',
      'department': 'Facilities/Operations/Energy Management',
      'description': 'Framework to improve energy efficiency across the EU.',
      'key_requirements': [
        'Conduct energy audits every 4 years (unless implementing ISO 50001)',
        'Implement identified energy efficiency measures where cost-effective',
        'Monitor and report energy consumption'
      ],
      'name': 'Energy Efficiency Directive'
    },
    {
      'deadlines': 'Ongoing compliance; documentation must be maintained for 10 years after product placed on market',
      'department': 'Product Safety/R&D/Quality',
      'description': 'Restricts use of specific hazardous materials in electrical and electronic equipment.',
      'key_requirements': [
        "Ensure products don't contain restricted substances above permitted levels",
        'Maintain technical documentation proving compliance',
        'Apply CE marking'
      ],
      'name': 'RoHS Directive (Restriction of Hazardous Substances)'
    }
  ],
  'summary': {
    'assessment': 'No assessment was provided.',
    'critical_compliance_areas': [
      'Environmental compliance (particularly EU ETS, Industrial Emissions Directive)',
      'Product safety and CE marking requirements',
      'GDPR data protection obligations',
      'Corporate sustainability reporting (CSRD implementation)',
      'Supply chain due diligence',
      'Occupational health and safety'
    ],
    'overall_assessment': 'As an Austrian manufacturing company with 251-1000 employees, your organization faces moderate to high regulatory complexity under EU frameworks. Your size places you in the medium-sized enterprise category, triggering numerous compliance obligations across environmental, product safety, labor, and data protection domains. Manufacturing in Austria means you must adhere to both EU-wide regulations and specific Austrian implementations of EU directives.',
    'recommended_strategy': 'Establish a cross-functional compliance team with representatives from Legal, HR, Operations, EHS, and IT departments. Implement a compliance management system aligned with ISO 19600 standards. Prioritize preparation for CSRD reporting requirements and the Corporate Sustainability Due Diligence Directive, as these represent significant new obligations for companies of your size. Conduct quarterly compliance reviews and maintain a centralized repository of regulatory requirements with clear ownership and deadlines. Consider investing in compliance management software to track the numerous deadlines and requirements across different regulatory domains.'
  },
  'timeline': [
    {
      'action_required': 'Conduct comprehensive energy audit by accredited auditor',
      'category': 'audit',
      'date': '2023-12-31',
      'department': 'Facilities/Operations',
      'description': 'Deadline for completing energy audit (if not implementing ISO 50001)',
      'formatted_date': '31 Dec 2023',
      'priority': 'high',
      'regulation': 'Energy Efficiency Directive'
    },
    {
      'action_required': 'Compile data for annual emissions report',
      'category': 'reporting',
      'date': '2024-01-31',
      'department': 'Environmental Health & Safety',
      'description': 'Preparation of annual emissions monitoring report',
      'formatted_date': '31 Jan 2024',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Submit verified emissions report to competent authority',
      'category': 'reporting',
      'date': '2024-03-31',
      'department': 'Environmental Health & Safety',
      'description': 'Submission deadline for verified annual emissions report',
      'formatted_date': '31 Mar 2024',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Surrender sufficient allowances to cover verified emissions',
      'category': 'implementation',
      'date': '2024-04-30',
      'department': 'Environmental Health & Safety/Finance',
      'description': 'Deadline for surrendering emission allowances',
      'formatted_date': '30 Apr 2024',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Review and update records of processing activities and privacy notices',
      'category': 'audit',
      'date': '2024-05-31',
      'department': 'Legal/IT/DPO',
      'description': 'Annual review of data processing activities',
      'formatted_date': '31 May 2024',
      'priority': 'medium',
      'regulation': 'GDPR'
    },
    {
      'action_required': 'Establish data collection systems for sustainability reporting',
      'category': 'implementation',
      'date': '2024-06-30',
      'department': 'Sustainability/Finance',
      'description': 'Begin data collection for first CSRD report',
      'formatted_date': '30 Jun 2024',
      'priority': 'high',
      'regulation': 'Corporate Sustainability Reporting Directive'
    },
    {
      'action_required': 'Ensure systems in place for tracking embedded emissions in imported goods',
      'category': 'implementation',
      'date': '2024-10-01',
      'department': 'Procurement/Environmental Health & Safety',
      'description': 'Transitional reporting period continues',
      'formatted_date': '1 Oct 2024',
      'priority': 'medium',
      'regulation': 'Carbon Border Adjustment Mechanism'
    },
    {
      'action_required': 'Implement due diligence system for relevant commodities',
      'category': 'implementation',
      'date': '2024-12-30',
      'department': 'Procurement/Sustainability',
      'description': 'Implementation deadline',
      'formatted_date': '30 Dec 2024',
      'priority': 'medium',
      'regulation': 'EU Deforestation Regulation'
    },
    {
      'action_required': 'Compile data for annual emissions report',
      'category': 'reporting',
      'date': '2025-01-31',
      'department': 'Environmental Health & Safety',
      'description': 'Preparation of annual emissions monitoring report',
      'formatted_date': '31 Jan 2025',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Submit verified emissions report to competent authority',
      'category': 'reporting',
      'date': '2025-03-31',
      'department': 'Environmental Health & Safety',
      'description': 'Submission deadline for verified annual emissions report',
      'formatted_date': '31 Mar 2025',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Surrender sufficient allowances to cover verified emissions',
      'category': 'implementation',
      'date': '2025-04-30',
      'department': 'Environmental Health & Safety/Finance',
      'description': 'Deadline for surrendering emission allowances',
      'formatted_date': '30 Apr 2025',
      'priority': 'high',
      'regulation': 'EU ETS'
    },
    {
      'action_required': 'Publish sustainability report in accordance with European Sustainability Reporting Standards',
      'category': 'reporting',
      'date': '2025-05-31',
      'department': 'Sustainability/Finance/Legal',
      'description': 'First CSRD report due (covering FY 2024)',
      'formatted_date': '31 May 2025',
      'priority': 'high',
      'regulation': 'Corporate Sustainability Reporting Directive'
    },
    {
      'action_required': 'Implement cost-effective energy efficiency measures identified in audit',
      'category': 'implementation',
      'date': '2025-12-31',
      'department': 'Facilities/Operations',
      'description': 'Deadline for implementing identified energy efficiency measures',
      'formatted_date': '31 Dec 2025',
      'priority': 'medium',
      'regulation': 'Energy Efficiency Directive'
    },
    {
      'action_required': 'Begin purchasing CBAM certificates for imported goods',
      'category': 'implementation',
      'date': '2026-01-01',
      'department': 'Procurement/Finance',
      'description': 'Full implementation begins',
      'formatted_date': '1 Jan 2026',
      'priority': 'high',
      'regulation': 'Carbon Border Adjustment Mechanism'
    },
    {
      'action_required': 'Publish sustainability report in accordance with European Sustainability Reporting Standards',
      'category': 'reporting',
      'date': '2026-05-31',
      'department': 'Sustainability/Finance/Legal',
      'description': 'Second CSRD report due (covering FY 2025)',
      'formatted_date': '31 May 2026',
      'priority': 'high',
      'regulation': 'Corporate Sustainability Reporting Directive'
    },
    {
      'action_required': 'Implement human rights and environmental due diligence system',
      'category': 'implementation',
      'date': '2027-01-01',
      'department': 'Legal/Procurement/Sustainability',
      'description': 'Expected implementation for companies of your size',
      'formatted_date': '1 Jan 2027',
      'priority': 'high',
      'regulation': 'Corporate Sustainability Due Diligence Directive'
    },
    {
      'action_required': 'Update technical documentation and conformity assessment procedures',
      'category': 'implementation',
      'date': '2027-01-17',
      'department': 'Product Safety/R&D/Quality',
      'description': 'New Machinery Regulation fully applicable',
      'formatted_date': '17 Jan 2027',
      'priority': 'high',
      'regulation': 'Machinery Regulation'
    }
  ],
  'upcoming_changes': [
    {
      'changes': [
        'Requires detailed reporting on environmental, social, and governance matters according to European Sustainability Reporting Standards.'
      ],
      'description': 'Expands sustainability reporting requirements to all large and listed companies.',
      'impact': 'High - Requires comprehensive sustainability data collection, verification, and reporting',
      'implementation_date': '2025-01-01 (for FY 2024 reporting for companies of your size)',
      'name': 'Corporate Sustainability Reporting Directive (CSRD)',
      'transition_period': 'Companies should begin preparation immediately to establish data collection systems'
    },
    {
      'changes': [
        'Introduces mandatory human rights and environmental due diligence obligations, including supply chain mapping, risk assessment, and remediation.'
      ],
      'description': 'Requires companies to identify, prevent, and mitigate adverse human rights and environmental impacts in their operations and supply chains.',
      'impact': 'High - Requires comprehensive supply chain mapping and due diligence systems',
      'implementation_date': '2027-01-01 (expected for companies of your size)',
      'name': 'Corporate Sustainability Due Diligence Directive (CSDDD)',
      'transition_period': '2-3 years after final adoption (expected adoption in 2024)'
    },
    {
      'changes': [
        'Requires due diligence system to ensure products are not linked to deforestation or forest degradation.'
      ],
      'description': 'Prohibits placing products linked to deforestation on the EU market.',
      'impact': 'Medium - Relevant if manufacturing involves wood, paper, rubber, palm oil, cattle, cocoa, coffee, or soy',
      'implementation_date': '2024-12-30',
      'name': 'EU Deforestation Regulation',
      'transition_period': '18 months for large companies after entry into force'
    },
    {
      'changes': [
        'New requirements for digital documentation, risk assessment for AI systems, and cybersecurity considerations.'
      ],
      'description': 'Updated framework for machinery safety, addressing new technologies and risks.',
      'impact': 'Medium-High - Requires updates to technical documentation and conformity assessment procedures',
      'implementation_date': '2027-01-17',
      'name': 'Machinery Regulation (replacing Machinery Directive)',
      'transition_period': '42 months after entry into force'
    },
    {
      'changes': [
        'Faster annual reduction of emission allowances, revised benchmarks, and changes to free allocation rules.'
      ],
      'description': 'Revised ETS with more stringent emission reduction targets.',
      'impact': 'High - Increased carbon costs and reduced free allocation for manufacturing sectors',
      'implementation_date': '2021-01-01 (ongoing through 2030)',
      'name': 'EU Emissions Trading System (EU ETS) Phase IV',
      'transition_period': 'Already in implementation phase'
    },
    {
      'changes': [
        'Importers must purchase CBAM certificates corresponding to embedded carbon emissions in certain products.'
      ],
      'description': 'System to price carbon emissions associated with imported goods.',
      'impact': 'Medium - May affect supply chain costs and competitiveness',
      'implementation_date': '2026-01-01 (full implementation); 2023-10-01 (transitional reporting)',
      'name': 'Carbon Border Adjustment Mechanism (CBAM)',
      'transition_period': 'Transitional phase from October 2023 to December 2025'
    },
    {
      'changes': [
        'New recyclability requirements, minimum recycled content targets, and packaging waste reduction goals.'
      ],
      'description': 'Updated rules on packaging design, waste reduction, and recycled content.',
      'impact': 'Medium - Requires packaging redesign and increased use of recycled materials',
      'implementation_date': '2025-2030 (phased implementation)',
      'name': 'Packaging and Packaging Waste Regulation',
      'transition_period': 'Various deadlines for different requirements'
    }
  ]
}